import { IconButton, Menu, MenuButton, MenuItem, MenuList, Tooltip } from '@chakra-ui/react';
import { FC, useCallback, useMemo } from 'react';
import { LuLayers } from 'react-icons/lu';
import { shallow } from 'zustand/shallow';
import {
  canReorderElement,
  isPinnedBackgroundLayer,
  type ElementReorderCommand,
} from '../../canvas/reorder';
import { useEditorStageView } from '../../contexts/EditorStageViewContext';
import { useStore } from '../../hooks/useStore';

type Props = {
  itemId: string;
  onOpenChange?: (isOpen: boolean) => void;
};

const ElementReorderMenu: FC<Props> = ({ itemId, onOpenChange }) => {
  const { isEditorStage } = useEditorStageView();
  const { elements, reorderElement } = useStore(
    (state) => ({
      elements: state.elements,
      reorderElement: state.reorderElement,
    }),
    shallow,
  );

  const element = useMemo(() => elements.find((el) => el.id === itemId), [elements, itemId]);
  const capabilities = canReorderElement(elements, itemId);
  const isPinnedBackground = Boolean(element && isPinnedBackgroundLayer(element));

  const handleReorder = useCallback(
    (command: ElementReorderCommand) => {
      reorderElement(itemId, command);
    },
    [itemId, reorderElement],
  );

  if (!isEditorStage || !element || isPinnedBackground) {
    return null;
  }

  return (
    <Menu
      isLazy
      placement="bottom-end"
      onOpen={() => onOpenChange?.(true)}
      onClose={() => onOpenChange?.(false)}
    >
      <Tooltip label="Move layer" placement="top" hasArrow openDelay={400}>
        <MenuButton
          as={IconButton}
          size="xs"
          icon={<LuLayers size="16" />}
          aria-label="Reorder element"
          colorScheme="gray"
          variant="ghost"
          rounded="full"
          data-test-id={`element-reorder-menu-${itemId}`}
          onClick={(evt) => evt.stopPropagation()}
        />
      </Tooltip>
      <MenuList fontSize="sm" zIndex="dropdown">
        <MenuItem
          isDisabled={!capabilities.front}
          data-test-id="element-reorder-front"
          onClick={() => handleReorder('front')}
        >
          Bring to front
        </MenuItem>
        <MenuItem
          isDisabled={!capabilities.forward}
          data-test-id="element-reorder-forward"
          onClick={() => handleReorder('forward')}
        >
          Bring forward
        </MenuItem>
        <MenuItem
          isDisabled={!capabilities.backward}
          data-test-id="element-reorder-backward"
          onClick={() => handleReorder('backward')}
        >
          Send backward
        </MenuItem>
        <MenuItem
          isDisabled={!capabilities.back}
          data-test-id="element-reorder-back"
          onClick={() => handleReorder('back')}
        >
          Send to back
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

ElementReorderMenu.displayName = 'ElementReorderMenu';

export default ElementReorderMenu;
